import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Container, Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem,
    Chip, Divider, Stack, IconButton, Tooltip, useMediaQuery, useTheme,
    CircularProgress, LinearProgress, Fade, Button, Alert, AlertTitle
} from '@mui/material';
import {
    ZoomIn, ZoomOut, CenterFocusStrong, Fullscreen, FullscreenExit,
    Layers, InfoOutlined, Map as MapIcon, Refresh,
    Download, DesktopWindows, CloudDownload, HighQuality
} from '@mui/icons-material';
import OpenSeadragon from 'openseadragon';
import TitleSection from '../utils/TittleTheme';
import { cidade } from '../adm/admin/firebaseConfig';

// ============================================================
// CONFIGURAÇÃO DE ARQUIVOS E ORTOMOSAICOS
// ============================================================
const ORTOMOSAICOS = {
    2026: [
        {
            id: 'orto-2026-principal',
            nome: 'Ortomosaico Principal 2026',
            url: '/imagens/mapeamento/ortomosaico_4k.webp',
        },
    ],
};

const ARQUIVOS_DOWNLOAD = [
    {
        label: 'Neo Ortomosaico (4K)',
        nome: 'Neo Ortomosaico - 4K',
        url: 'https://drive.google.com/file/d/1GYNBk6-Xh6Y6u1svoJpqpi8sVliNap5b/view?usp=sharing',
        badge: '4K',
    },

    {
        label: 'Neo Ortomosaico Legendado (4K)',
        nome: 'Neo Ortomosaico Legendado - 4K',
        url: 'https://drive.google.com/file/d/1lAQF71BYSOVW8y-ASDDNpWt4o4jq59Uf/view?usp=sharing',
        badge: '4K',
    },

    {
        label: 'Landscape Gurjão (8K)',
        nome: 'Landscape Gurjao 8K Logo Inferior.jpeg',
        url: 'https://drive.google.com/file/d/1Pccnpkqsvq0voJAHm2HuXNGQfgUWepbs/view?usp=sharing',
        badge: '8K',
    },
    {
        label: 'Landscape Gurjão (4K)',
        nome: 'Landscape Gurjao 4K.jpeg',
        url: 'https://drive.google.com/file/d/1KNAcID5TQ6iZlt3AW_hH2eQg1TdXG4d7/view?usp=sharing',
        badge: '4K',
    },
];

const ANOS_DISPONIVEIS = Object.keys(ORTOMOSAICOS)
    .map(Number)
    .sort((a, b) => b - a);

const POSSIBLE_PATHS = [
    '/imagens/mapeamento/ortomosaico_gurjao_2026.jpeg',
    '/imagens/ortomosaico_gurjao_2026.jpeg',
    '/ortomosaico_gurjao_2026.jpeg',
    '/assets/mapeamento/ortomosaico_gurjao_2026.jpeg',
    '/public/imagens/mapeamento/ortomosaico_gurjao_2026.jpeg',
];

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const MapeamentoDigitalPage = ({ config }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const viewerRef = useRef(null);
    const osdRef = useRef(null);

    const [anoSelecionado, setAnoSelecionado] = useState(ANOS_DISPONIVEIS[0] || 2026);
    const [ortomosaicoAtual, setOrtomosaicoAtual] = useState(
        ORTOMOSAICOS[ANOS_DISPONIVEIS[0]]?.[0] || null
    );

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [loadProgress, setLoadProgress] = useState(0);
    const [currentPath, setCurrentPath] = useState('');

    // ----------------------------------------------------------
    // Inicializa OpenSeadragon
    // ----------------------------------------------------------
    const initViewer = useCallback((imageUrl) => {
        if (!viewerRef.current) return;

        if (osdRef.current) {
            osdRef.current.destroy();
            osdRef.current = null;
        }

        setImageLoading(true);
        setImageError(false);
        setLoadProgress(5);
        setCurrentPath(imageUrl);

        const progressTimer = setInterval(() => {
            setLoadProgress((prev) => {
                if (prev >= 88) {
                    clearInterval(progressTimer);
                    return 88;
                }
                return prev + Math.random() * 5 + 1.2;
            });
        }, 500);

        try {
            const viewer = OpenSeadragon({
                element: viewerRef.current,
                prefixUrl: 'https://cdnjs.cloudflare.com/ajax/libs/openseadragon/4.1.1/images/',
                tileSources: {
                    type: 'image',
                    url: imageUrl,
                },
                showNavigationControl: false,
                showNavigator: !isMobile,
                navigatorPosition: 'BOTTOM_LEFT',
                navigatorSizeRatio: 0.17,
                maxZoomPixelRatio: 2.5,
                minZoomImageRatio: 0.8,
                defaultZoomLevel: 0, // Enquadramento inicial otimizado
                visibilityRatio: 1,
                constrainDuringPan: true,
                animationTime: 0.5,
                springStiffness: 7,
                immediateRender: false,
                imageLoaderLimit: 2,
                maxImageCacheCount: 100,
                timeout: 180000,
                crossOriginPolicy: false,
                placeholderFillStyle: '#0d1117',
                blendTime: 0.3,
            });

            osdRef.current = viewer;

            viewer.addHandler('open', () => {
                clearInterval(progressTimer);
                setLoadProgress(100);

                // Garante centralização e enquadramento suave da imagem
                viewer.viewport.goHome(true);

                setTimeout(() => setImageLoading(false), 500);
            });

            viewer.addHandler('open-failed', (event) => {
                clearInterval(progressTimer);
                console.error('OpenSeadragon open-failed:', event);
                setImageLoading(false);
                setImageError(true);
            });

        } catch (err) {
            clearInterval(progressTimer);
            console.error(err);
            setImageLoading(false);
            setImageError(true);
        }
    }, [isMobile]);

    // ----------------------------------------------------------
    // Tenta carregar imagem através dos caminhos possíveis
    // ----------------------------------------------------------
    const tryLoadImage = useCallback(async () => {
        setImageLoading(true);
        setImageError(false);
        setLoadProgress(0);

        const pathsToTry = [
            ortomosaicoAtual?.url,
            ...POSSIBLE_PATHS,
        ].filter(Boolean);

        for (const path of pathsToTry) {
            try {
                const response = await fetch(path, { method: 'HEAD' });
                if (response.ok) {
                    console.log('Imagem encontrada em:', path);
                    initViewer(path);
                    return;
                }
            } catch (e) {
                // Continua tentando os demais caminhos
            }
        }

        setImageLoading(false);
        setImageError(true);
    }, [ortomosaicoAtual, initViewer]);

    // ----------------------------------------------------------
    // Efeitos
    // ----------------------------------------------------------
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    useEffect(() => {
        if (ortomosaicoAtual) {
            tryLoadImage();
        }

        return () => {
            if (osdRef.current) {
                osdRef.current.destroy();
                osdRef.current = null;
            }
        };
    }, [ortomosaicoAtual, tryLoadImage]);

    useEffect(() => {
        const lista = ORTOMOSAICOS[anoSelecionado] || [];
        setOrtomosaicoAtual(lista[0] || null);
    }, [anoSelecionado]);

    // ----------------------------------------------------------
    // Controles de Visão
    // ----------------------------------------------------------
    const handleZoomIn = () => osdRef.current?.viewport.zoomBy(1.4);
    const handleZoomOut = () => osdRef.current?.viewport.zoomBy(0.7);
    const handleResetView = () => osdRef.current?.viewport.goHome(true);

    const toggleFullscreen = () => {
        const el = viewerRef.current?.parentElement;
        if (!el) return;

        if (!document.fullscreenElement) {
            el.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    // ----------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------
    return (
        <Box sx={{ bgcolor: '#f4f6f9', minHeight: '100vh', pb: 8 }}>
            <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 5 }, px: { xs: 2, md: 3 } }}>

                <Box sx={{ mb: 3 }}>
                    <TitleSection
                        title="Mapeamento Digital"
                        subtitle={`Ortomosaico aéreo de alta resolução – Município de ${cidade || 'Gurjão'}`}
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        alignItems: 'flex-start',
                    }}
                >
                    {/* ===================== MAPA PRINCIPAL ===================== */}
                    <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                        <Paper
                            elevation={4}
                            sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                position: 'relative',
                                bgcolor: '#0d1117',
                            }}
                        >
                            {/* Controles de navegação do mapa */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 14,
                                    right: 14,
                                    zIndex: 1100,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.8,
                                }}
                            >
                                <Tooltip title="Aproximar" placement="left">
                                    <IconButton
                                        size="small"
                                        onClick={handleZoomIn}
                                        disabled={imageLoading}
                                        sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                        <ZoomIn fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Afastar" placement="left">
                                    <IconButton
                                        size="small"
                                        onClick={handleZoomOut}
                                        disabled={imageLoading}
                                        sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                        <ZoomOut fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Centralizar" placement="left">*
                                    <IconButton
                                        size="small"
                                        onClick={handleResetView}
                                        disabled={imageLoading}
                                        sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                        <CenterFocusStrong fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'} placement="left">
                                    <IconButton
                                        size="small"
                                        onClick={toggleFullscreen}
                                        sx={{ bgcolor: 'white', boxShadow: 3, '&:hover': { bgcolor: '#f5f5f5' } }}
                                    >
                                        {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Badge de Identificação */}
                            <Box sx={{ position: 'absolute', top: 14, left: 14, zIndex: 1100 }}>
                                <Chip
                                    icon={<Layers sx={{ fontSize: 16 }} />}
                                    label={ortomosaicoAtual?.nome || `Ano ${anoSelecionado}`}
                                    sx={{
                                        bgcolor: 'rgba(0,0,0,0.78)',
                                        color: 'white',
                                        fontWeight: 600,
                                        backdropFilter: 'blur(8px)',
                                    }}
                                />
                            </Box>

                            {/* Overlay de Carregamento */}
                            <Fade in={imageLoading}>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 1200,
                                        bgcolor: 'rgba(13, 17, 23, 0.94)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 3,
                                        px: 3,
                                    }}
                                >
                                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={loadProgress}
                                            size={88}
                                            thickness={3.2}
                                            sx={{ color: '#4fc3f7' }}
                                        />
                                        <Box
                                            sx={{
                                                top: 0, left: 0, bottom: 0, right: 0,
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="caption" color="white" fontWeight={700} fontSize={15}>
                                                {`${Math.round(loadProgress)}%`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ textAlign: 'center', maxWidth: 380 }}>
                                        <Typography variant="h6" color="white" fontWeight={600} gutterBottom>
                                            Carregando Ortomosaico 4K
                                        </Typography>
                                        <Typography variant="body2" color="rgba(255,255,255,0.72)" sx={{ mb: 2.5 }}>
                                            Arquivo em altíssima definição.
                                            O carregamento pode levar alguns segundos dependendo da sua conexão.
                                        </Typography>

                                        <LinearProgress
                                            variant="determinate"
                                            value={loadProgress}
                                            sx={{
                                                height: 7,
                                                borderRadius: 4,
                                                bgcolor: 'rgba(255,255,255,0.12)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    background: 'linear-gradient(90deg, #0288d1, #4fc3f7)',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Fade>

                            {/* Tela de Erro ao Carregar */}
                            {imageError && !imageLoading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 1200,
                                        bgcolor: 'rgba(13, 17, 23, 0.96)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2.5,
                                        px: 3,
                                        textAlign: 'center',
                                    }}
                                >
                                    <MapIcon sx={{ fontSize: 64, color: '#ef5350' }} />
                                    <Typography variant="h6" color="white" fontWeight={600}>
                                        Não foi possível carregar o ortomosaico
                                    </Typography>
                                    <Typography variant="body2" color="rgba(255,255,255,0.75)" sx={{ maxWidth: 420 }}>
                                        O arquivo de alta resolução não pôde ser encontrado ou acessado pelo navegador.
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        startIcon={<Refresh />}
                                        onClick={tryLoadImage}
                                        sx={{ mt: 1 }}
                                    >
                                        Tentar novamente
                                    </Button>

                                    <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ mt: 2 }}>
                                        Caminho tentado: {currentPath || 'nenhum'}
                                    </Typography>
                                </Box>
                            )}

                            {/* Container do OpenSeadragon */}
                            <Box
                                ref={viewerRef}
                                sx={{
                                    width: '100%',
                                    height: { xs: '58vh', sm: '68vh', md: '75vh' },
                                    minHeight: 400,
                                    bgcolor: '#0d1117',
                                }}
                            />
                        </Paper>

                        {/* LEGENDA DE QUALIDADE E AVISO ABAIXO DO ORTOMOSAICO */}
                        <Alert
                            severity="info"
                            icon={<HighQuality sx={{ color: '#0288d1' }} />}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                border: '1px solid rgba(2, 136, 209, 0.2)',
                                bgcolor: 'rgba(2, 136, 209, 0.05)',
                                '& .MuiAlert-message': { width: '100%' }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#01579b', mb: 0.3 }}>
                                Resolução 4K e Otimização para Web
                            </AlertTitle>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.84rem', lineHeight: 1.5 }}>
                                A imagem exibida no visualizador acima é derivada de um ortomosaico em <strong>4K</strong>, porém passou por um processo de compressão inteligente para possibilitar uma navegação fluida na web. Para visualizar ou analisar os detalhes em <strong>máxima fidelidade original e alta qualidade</strong>, recomendamos realizar o download do arquivo.
                            </Typography>
                        </Alert>
                    </Box>

                    {/* ===================== SIDEBAR ===================== */}
                    <Stack
                        spacing={3}
                        sx={{
                            width: { xs: '100%', md: 320 },
                            flexShrink: 0,
                            position: { md: 'sticky' },
                            top: { md: 24 },
                        }}
                    >
                        {/* CARD 1: Informações e Responsáveis */}
                        <Paper
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                p: 2.5,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">
                                Ano do Levantamento
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
                                <InputLabel id="ano-label">Selecionar ano</InputLabel>
                                <Select
                                    labelId="ano-label"
                                    value={anoSelecionado}
                                    label="Selecionar ano"
                                    onChange={(e) => setAnoSelecionado(e.target.value)}
                                >
                                    {ANOS_DISPONIVEIS.map((ano) => (
                                        <MenuItem key={ano} value={ano}>
                                            {ano}
                                            {ano === Math.max(...ANOS_DISPONIVEIS) && (
                                                <Chip label="Mais recente" size="small" color="success" sx={{ ml: 1, height: 20 }} />
                                            )}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">
                                Responsáveis Técnicos
                            </Typography>

                            <Stack spacing={2.2} sx={{ mt: 1.5 }}>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>Thiago Reis da Silva</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Aerolevantamento e Processamento Digital
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="body2" fontWeight={600}>Alex Emiliano de Souza</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Projeto de Linhas e Avaliação
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 2.5 }} />

                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55, display: 'block' }}>
                                Levantamento aerofotogramétrico realizado com drone de alta precisão.
                                O ortomosaico permite visualização detalhada do território municipal.
                            </Typography>
                        </Paper>

                        {/* CARD 2: Downloads */}
                        <Paper
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                p: 2.5,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CloudDownload fontSize="small" /> Downloads
                            </Typography>

                            {/* Aviso Desktop / PC */}
                            <Box
                                sx={{
                                    p: 1.5,
                                    mb: 2,
                                    bgcolor: 'rgba(255, 152, 0, 0.08)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(255, 152, 0, 0.25)',
                                }}
                            >
                                <Typography variant="caption" color="warning.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, fontWeight: 700 }}>
                                    <DesktopWindows fontSize="small" /> Recomendação
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.74rem', lineHeight: 1.4 }}>
                                    Recomendamos utilizar um computador/desktop para melhor visualização e download dos arquivos em alta resolução.
                                </Typography>
                            </Box>

                            {/* Lista de Arquivos para Download */}
                            <Stack spacing={1.2}>
                                {ARQUIVOS_DOWNLOAD.map((arq, index) => (
                                    <Button
                                        key={index}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        startIcon={<Download fontSize="small" />}
                                        component="a"
                                        href={arq.url}
                                        download={arq.nome}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            justifyContent: 'space-between',
                                            textAlign: 'left',
                                            textTransform: 'none',
                                            fontSize: '0.78rem',
                                            py: 1,
                                            px: 1.5,
                                            borderRadius: 2,
                                            borderColor: 'rgba(0, 0, 0, 0.12)',
                                            color: 'text.primary',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: 'rgba(2, 136, 209, 0.04)',
                                            },
                                        }}
                                    >
                                        <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pr: 1 }}>
                                            {arq.label}
                                        </Box>
                                        <Chip label={arq.badge} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                                    </Button>
                                ))}
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>

                {/* ===================== LANDSCAPE INFERIOR REFINADO ===================== */}
                <Paper
                    elevation={4}
                    sx={{
                        mt: 6,
                        borderRadius: 4,
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: { xs: 340, sm: 440, md: 520 },
                        backgroundImage: `linear-gradient(to top, rgba(13, 17, 23, 0.95) 0%, rgba(13, 17, 23, 0.55) 50%, rgba(0, 0, 0, 0.2) 100%),
                               url('/imagens/mapeamento/mapeamento.webp')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'flex-end',
                        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                        '&:hover': {
                            boxShadow: 10,
                        },
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 3, sm: 4, md: 5 },
                            color: 'white',
                            maxWidth: 850,
                            width: '100%',
                        }}
                    >
                        <Chip
                            icon={<HighQuality sx={{ fontSize: '1rem !important', color: 'white !important' }} />}
                            label="Visão Panorâmica • 8K"
                            size="small"
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                fontSize: '0.72rem',
                                letterSpacing: '0.05em',
                                bgcolor: 'rgba(2, 136, 209, 0.85)',
                                color: 'white',
                                backdropFilter: 'blur(6px)',
                            }}
                        />

                        {/* Fonte e Tipografia Refinadas */}
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.15,
                                fontSize: { xs: '1.75rem', sm: '2.3rem', md: '2.75rem' },
                                fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", sans-serif',
                                textShadow: '0px 2px 12px rgba(0,0,0,0.6)',
                                mb: 1.5,
                            }}
                        >
                            Conheça Gurjão de cima
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                opacity: 0.92,
                                lineHeight: 1.7,
                                fontSize: { xs: '0.94rem', md: '1.08rem' },
                                fontWeight: 400,
                                maxWidth: 720,
                                mb: 3,
                                textShadow: '0px 1px 6px rgba(0,0,0,0.5)',
                            }}
                        >
                            O mapeamento digital e as capturas ortomosaicas de alta precisão permitem à administração e aos cidadãos explorar o território municipal com incrível riqueza de detalhes, impulsionando a gestão urbana, o planejamento territorial e a transparência pública.
                        </Typography>

                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Download />}
                            component="a"
                            href="https://drive.google.com/file/d/1Pccnpkqsvq0voJAHm2HuXNGQfgUWepbs/view?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                bgcolor: '#0288d1',
                                color: 'white',
                                fontWeight: 700,
                                px: 3.5,
                                py: 1.3,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                                backdropFilter: 'blur(4px)',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    bgcolor: '#0277bd',
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            Baixar Arquivo em 8K
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default MapeamentoDigitalPage;